import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import deepEqual from 'fast-deep-equal';
import { SearchInput } from './components/search_input';
import { SearchResults, SEARCH_ERROR_MESSAGE, clampExcerptLines } from './components/search_results';
import { SearchPagination } from './components/search_pagination';
import { InlineSearch } from './components/inline_search';
import { TileOverlay } from './components/tile_overlay';
import { BarTrigger } from './components/bar_trigger';
import { ResponsiveStyles, ScopedOverlayStyles, InstanceStyles, INPAGE_SURFACE_CSS, SEARCH_INPUT_CSS, INLINE_RESULTS_CSS, TRIGGER_CSS, SHADOW_SURFACE_RESET_CSS, THEME_VARIANT_CSS } from './components/responsive_styles';
import { use_search } from './hooks/use_search';
import { use_base_url } from './hooks/use_base_url';
import { useNarrowViewport } from './hooks/use_narrow_viewport';
import { getTheme, resolveTheme, getSystemTheme } from './themes';
import { hexToRgbTriple } from './theme_tokens.js';
import { fetchSharedConfig, sendClientErrorBeacon } from './search_fetch';
import { debugLog, debugWarn } from './debug';
import { overlayPortalTarget, isScopedOverlay } from './overlay_portal';

/**
 * SearchConfigError renders the widget's terminal error state when the index
 * config cannot be loaded (after retry). It shows friendly copy only -- the raw
 * failure detail goes to the console/beacon, never on screen -- and announces
 * itself to assistive tech via role="alert".
 */
// The render configuration is three independent server attributes (decision
// #4963). Each names what the visitor actually sees, and no value is shared
// between them -- a single token that could be read as either a mode or a
// placement is what caused the repeated demo/display_mode conflation.
//
//   trigger: what the visitor sees before searching
//   surface: where the search UI appears once engaged
//   layout:  how results are presented
export const TRIGGERS = ['icon', 'bar', 'none'];
export const SURFACES = ['overlay', 'attached', 'inpage'];
export const LAYOUTS = ['list', 'grid'];

const DEFAULT_SHAPE = { trigger: 'icon', surface: 'overlay', layout: 'list' };

// Legacy displayMode -> attribute triple. Mirrors the server-side mapping in
// tripleForLegacyDisplayMode so a stale cached config resolves the same way the
// migration would resolve the row.
const LEGACY_DISPLAY_MODE_SHAPES = {
  modal: { trigger: 'icon', surface: 'overlay', layout: 'list' },
  inline: { trigger: 'bar', surface: 'attached', layout: 'list' },
  tile: { trigger: 'icon', surface: 'overlay', layout: 'grid' },
};

// resolveRenderShape derives the widget's render shape from server config and
// NOTHING else.
//
// The attributes are authoritative. displayMode/isModal are consulted only when
// the triple is absent entirely, which means a config payload predating the
// attributes. There is no client-side override: a `mode` prop is not an input,
// and `demoMode` -- autoplay, fixtures, analytics suppression -- has no
// rendering role at any point.
//
// Unknown values fall back to their attribute's default rather than blanking
// the widget, and an incoherent pair is repaired: trigger 'none' forces the
// 'inpage' surface, because with no trigger an overlay would leave the visitor
// no way to reach the search UI at all.
export function resolveRenderShape(config = {}) {
  const { trigger, surface, layout, displayMode, isModal } = config || {};

  const hasAttribute = trigger != null || surface != null || layout != null;
  if (!hasAttribute) {
    if (LEGACY_DISPLAY_MODE_SHAPES[displayMode]) return { ...LEGACY_DISPLAY_MODE_SHAPES[displayMode] };
    if (isModal === false) return { ...LEGACY_DISPLAY_MODE_SHAPES.inline };
    return { ...DEFAULT_SHAPE };
  }

  const shape = {
    trigger: TRIGGERS.includes(trigger) ? trigger : DEFAULT_SHAPE.trigger,
    surface: SURFACES.includes(surface) ? surface : DEFAULT_SHAPE.surface,
    layout: LAYOUTS.includes(layout) ? layout : DEFAULT_SHAPE.layout,
  };

  if (shape.trigger === 'none') shape.surface = 'inpage';

  return shape;
}

// Bounds and default for the narrow-viewport breakpoint. Mirrors the server's
// clamp so a config that somehow arrives out of range resolves identically on
// both sides.
const NARROW_BREAKPOINT_DEFAULT_PX = 768;
const NARROW_BREAKPOINT_MIN_PX = 320;
const NARROW_BREAKPOINT_MAX_PX = 1200;

/**
 * resolveNarrowShape derives the render shape to use below the breakpoint, from
 * server config and NOTHING else.
 *
 * Responsive rendering is a SECOND server-owned triple (decision #5057), not a
 * client-side override: the operator configures both shapes in the dashboard,
 * and the widget only evaluates which one the current viewport selects. That is
 * what keeps decision #4963 intact.
 *
 * Returns null when the instance declares no collapse -- the overwhelmingly
 * common case, and the reason no existing instance changes how it renders. A
 * partial triple fills from the BASE shape rather than the global defaults, so
 * an operator who set only the small-screen trigger keeps the layout they
 * chose. Unknown values and incoherent pairs are repaired exactly as
 * resolveRenderShape repairs them.
 *
 * @param {Object} [config] - The server config payload
 * @returns {{trigger: string, surface: string, layout: string}|null}
 */
export function resolveNarrowShape(config = {}) {
  const { narrowTrigger, narrowSurface, narrowLayout } = config || {};

  const declared = [narrowTrigger, narrowSurface, narrowLayout].some((v) => v != null && v !== '');
  if (!declared) return null;

  const base = resolveRenderShape(config);
  const shape = {
    trigger: TRIGGERS.includes(narrowTrigger) ? narrowTrigger : null,
    surface: SURFACES.includes(narrowSurface) ? narrowSurface : null,
    layout: LAYOUTS.includes(narrowLayout) ? narrowLayout : null,
  };

  // An unknown value falls back to its attribute's DEFAULT, matching
  // resolveRenderShape; an absent one fills from the base shape. The two cases
  // differ: a value the operator never set is not a value the operator got
  // wrong.
  if (shape.trigger == null) shape.trigger = narrowTrigger == null || narrowTrigger === '' ? base.trigger : DEFAULT_SHAPE.trigger;
  if (shape.surface == null) shape.surface = narrowSurface == null || narrowSurface === '' ? base.surface : DEFAULT_SHAPE.surface;
  if (shape.layout == null) shape.layout = narrowLayout == null || narrowLayout === '' ? base.layout : DEFAULT_SHAPE.layout;

  if (shape.trigger === 'none') shape.surface = 'inpage';

  return shape;
}

/**
 * narrowBreakpointFor returns the max viewport width, in CSS px, at which the
 * narrow shape applies -- clamped to the supported range, with anything unset
 * or non-numeric resolving to the default rather than producing a NaN media
 * query that would silently disable the collapse.
 *
 * @param {Object} [config] - The server config payload
 * @returns {number}
 */
export function narrowBreakpointFor(config = {}) {
  const px = Number((config || {}).narrowBreakpointPx);
  if (!Number.isFinite(px) || px <= 0) return NARROW_BREAKPOINT_DEFAULT_PX;
  return Math.min(Math.max(px, NARROW_BREAKPOINT_MIN_PX), NARROW_BREAKPOINT_MAX_PX);
}

// rendererForShape names which of the three shipped renderers a shape selects.
//
// Selecting is all it does. The modal, tile, and InlineSearch components are
// untouched by the attribute work (decision #4963), so every shape a customer
// is on today lands on exactly the component it lands on today:
//
//   icon/overlay/list -> modal        icon/overlay/grid -> tile
//   bar/attached/list -> InlineSearch
//
// The inpage surface is the fourth renderer: the search UI in the document
// flow, always open and with no trigger, in either layout.
//
// Surface is read before layout throughout, because the surface is what picks
// the renderer and the layout is what that renderer presents. An attached grid
// is InlineSearch showing tile cards anchored to the bar, not a grid that has
// escaped into a fullscreen overlay.
// resolvePageSize says how many hits one page holds.
//
// For every surface but the attached dropdown that is just `resultsPerPage`,
// the instance's own paging size. The attached surface has a second control,
// `inlineMaxResults`, which capInlineResults applies as a client-side ceiling
// AFTER the request -- correct while the dropdown has no pager, since it means
// "show me at most N of whatever came back".
//
// Once a pager renders (item #16239) that reading breaks: paging through
// 10-result pages while displaying 5 of each hides half the corpus behind a
// Next button, and has_next_page divides by a page size the visitor never saw.
// So with pagination on, the cap IS the page size -- which also makes
// capInlineResults a no-op against a full page rather than a truncation.
//
// Scoped to mode === 'inline' deliberately: this is the attached dropdown's
// control. Reinterpreting it globally would repage live modal instances that
// never asked for it.
//
// The 1-20 bound mirrors the server's clamp (index.go:377-378) rather than
// trusting the payload, because a row stored before that clamp still
// deserializes through here.
export function resolvePageSize(config = {}) {
  if (config.mode !== 'inline' || !config.enablePagination) {
    return config.resultsPerPage;
  }
  const cap = Math.floor(config.inlineMaxResults);
  if (!Number.isFinite(cap) || cap < 1) {
    return config.resultsPerPage;
  }
  return Math.min(cap, 20);
}

/**
 * Resolve a boolean config toggle across the embed, the server and a default.
 *
 * Booleans need their own resolver because the two obvious spellings are both
 * wrong for a toggle whose default is ON. `firstNonEmpty`, which the colour and
 * length fields use, keeps `false` as a value -- correct for a tri-state and
 * correct here for the ui/apiConfig steps, but it has no notion of a default to
 * fall back to. `||` collapses `false` into "absent", which makes an explicit
 * off unreachable: the single defect a default-true toggle exists to avoid.
 *
 * `??` is the one that treats only null/undefined as absent, which is what
 * "the operator did not say" actually means here.
 *
 * The fallback is the compatibility floor. A bundle talking to a config that
 * predates the key gets it, so an older server never silently changes what a
 * deployed widget renders (items #17160, #17161).
 *
 * @param {boolean|undefined|null} embedValue - the value passed as a prop
 * @param {boolean|undefined|null} serverValue - the value from the public config
 * @param {boolean} fallback - what this toggle means when nobody has said
 * @returns {boolean}
 */
export function resolveToggle(embedValue, serverValue, fallback) {
  return Boolean(embedValue ?? serverValue ?? fallback);
}

export function rendererForShape(shape = {}) {
  const { surface, layout } = shape || {};
  if (surface === 'attached') return 'inline';
  if (surface === 'inpage') return 'inpage';
  if (layout === 'grid') return 'tile';
  return 'modal';
}

export function SearchConfigError() {
  return (
    <div className="vo-search-error-message" role="alert">
      {SEARCH_ERROR_MESSAGE}
    </div>
  );
}

/**
 * VisitingObjectsSearch - A React component for integrating Visiting Objects search functionality
 * @param {Object} props
 * @param {string} props.clientPublicToken - The public token for authentication
 * @param {boolean} props.isModal - Whether the search is displayed in a modal (default: true)
 * @param {string} props.primaryColor - Primary color for UI elements
 * @param {string} props.modalTriggerIconColor - Color for the search icon in the modal trigger
 * @param {string} props.backgroundColor - Background color for the search modal
 * @param {string} props.textColor - Text color for the search modal
 * @param {string} props.inputBackgroundColor - Background color for the search input
 * @param {string} props.inputTextColor - Text color for the search input
 * @param {string} props.paginationButtonBgColor - Background color for pagination buttons
 * @param {string} props.paginationIconColor - Color for pagination icons
 * @param {string} props.borderColor - Border color for the search modal
 * @param {string} props.refParam - Reference parameter for tracking
 * @param {boolean} props.showDefaultResults - Whether to show default results
 * @param {string} props.resultLinkRootPath - Root path for result links
 * @param {boolean} props.pushState - Whether to update URL state
 * @param {number} props.resultsPerPage - Number of results per page
 * @param {boolean} props.autoFocus - Whether to autofocus the search input
 * @param {boolean} props.enablePagination - Whether to enable pagination
 * @param {boolean} props.hideAttribution - Whether to hide attribution
 * @param {function} props.onClose - Callback when modal is closed (only used in modal mode)
 * @param {string} props.initial_query - Initial query for the search
 * @param {number} props.initial_page - Initial page for the search
 * @param {string} props.domReplacementRules - JSON string of DOM replacement rules
 * @param {Object} props.ui - UI overrides for the search component
 * @param {boolean} props.demoMode - Whether to enable demo functionality
 * @param {boolean} props.hideTrigger - Whether to hide the modal trigger button (used when triggers are placed externally via DOM rules)
 */
export default function VisitingObjectsSearch({
  clientPublicToken,
  ui = {},
  initial_query = '',
  initial_page = 1,
  demoMode = false,
  // The demo's per-instance event channel (item #8838). DemoSearch creates one
  // and passes it in; it reaches SearchInput wherever the resolved shape puts
  // it. Null for every customer embed, which has no autoplay to carry.
  demoTransport = null,
  hideTrigger = false,
  shapeOverride = null,
  // {shape, breakpointPx} for a placement that collapses on its own terms.
  // Null means this placement does not collapse, which is not the same as
  // deferring to the instance's collapse.
  narrowOverride = null,
  // Where an overlay surface portals. Null means document.body, which is where
  // every overlay has always gone and where one stays unless its placement asks
  // otherwise. Per decision #5067 this moves the overlay's DOM node; it changes
  // neither the shape nor whether the overlay is open.
  portalTarget = null
}) {
  const baseUrl = use_base_url();
  // State to store API config
  const [apiCfg, setApiCfg] = useState(null);
  // Set when the config fetch fails after its retry, so we render an error
  // state instead of silently returning null.
  const [configError, setConfigError] = useState(false);

  // Get initial state from URL if pushState is enabled
  const getInitialStateFromUrl = useCallback(() => {
    // Check if we're in a browser environment
    if (typeof window === 'undefined' || !window.location?.href) {
      return {
        query: initial_query || '',
        page: parseInt(initial_page || 1, 10)
      };
    }
    
    const url = new URL(window.location.href);
    return {
      query: url.searchParams.get('q') || initial_query || '',
      page: parseInt(url.searchParams.get('page') || initial_page || 1, 10)
    };
  }, [initial_query, initial_page]);

  // Get initial state
  const initialState = getInitialStateFromUrl();

  // Fetch API config once on component mount. fetchConfig checks response.ok
  // and retries once with backoff; a final failure surfaces an error state
  // rather than leaving the widget silently unrendered.
  useEffect(() => {
    if (!clientPublicToken) return;

    debugLog('[vo-search-component] Fetching API config with clientPublicToken:', clientPublicToken);

    let cancelled = false;

    fetchSharedConfig(
      `${baseUrl}/api/search/index/config`,
      clientPublicToken,
      () => {
        const formData = new FormData();
        formData.append('clientPublicToken', clientPublicToken);
        return formData;
      }
    )
      .then(data => {
        if (cancelled) return;
        debugLog('[vo-search-component] Received API config:', {
          theme: data.theme,
          showDefaultResults: data.showDefaultResults,
          primaryColor: data.primaryColor,
          backgroundColor: data.backgroundColor
        });
        setConfigError(false);
        setApiCfg(data);
      })
      .catch(error => {
        if (cancelled) return;
        console.error('Error fetching search config:', error);
        sendClientErrorBeacon({
          baseUrl,
          token: clientPublicToken,
          errorClass: 'config_fetch',
          status: error?.status || 0,
          message: error?.message || '',
        });
        setConfigError(true);
      });

    return () => { cancelled = true; };
  }, [clientPublicToken, baseUrl]);

  // Merge API config with UI overrides - set default isModal to true if not specified
  const apiStable = useRef();
  if (apiCfg && !deepEqual(apiCfg, apiStable.current)) {
    debugLog('[vo-search-component] Updating apiStable ref with new API config');
    apiStable.current = apiCfg;
  }

  // Track system theme for 'auto' mode (default when theme is empty)
  const [systemTheme, setSystemTheme] = useState(() =>
    typeof window !== 'undefined' ? getSystemTheme() : 'light'
  );

  // Listen for system preference changes
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    // Legacy Safari
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  // The narrow shape is resolved BEFORE the merged config, because subscribing
  // to the viewport is a hook and hooks cannot run inside a useMemo. The
  // resulting flag feeds back into that memo below.
  //
  // shapeOverride short-circuits the whole thing: where VO's own chrome owns the
  // slot, the slot's shape wins at every width (decision #5050), so there is no
  // subscription to make.
  const shapeConfig = useMemo(
    () => ({ ...(apiStable.current || {}), ...ui }),
    [apiStable.current, ui]
  );
  // A placement brings its own collapse, or brings none and therefore does not
  // collapse at all -- decision #5057's opt-in default, one level down.
  // Inheriting the instance's collapse would silently change how a live
  // placement renders on a phone.
  const narrowShape = useMemo(() => {
    if (narrowOverride && narrowOverride.shape) return resolveRenderShape(narrowOverride.shape);
    if (shapeOverride) return null;
    return resolveNarrowShape(shapeConfig);
  }, [shapeConfig, shapeOverride, narrowOverride]);

  const narrowBreakpointPx = useMemo(() => {
    if (narrowOverride && narrowOverride.shape) {
      return narrowBreakpointFor({ narrowBreakpointPx: narrowOverride.breakpointPx });
    }
    return narrowBreakpointFor(shapeConfig);
  }, [shapeConfig, narrowOverride]);

  const isNarrowViewport = useNarrowViewport(narrowShape ? narrowBreakpointPx : null);

  const merged = useMemo(() => {
    const apiConfig = apiStable.current || {};

    // Resolve theme - handle auto and vo-auto using current system theme for reactivity
    const rawTheme = ui.theme || apiConfig.theme || 'auto';
    let resolvedTheme;
    if (!rawTheme || rawTheme === 'auto') {
      resolvedTheme = systemTheme; // 'light' or 'dark'
    } else if (rawTheme === 'vo-auto') {
      resolvedTheme = systemTheme === 'dark' ? 'vo-dark' : 'vo-light';
    } else {
      resolvedTheme = rawTheme; // Use theme as-is (light, dark, vo-light, vo-dark)
    }
    const themeDefaults = getTheme(resolvedTheme);

    // Helper to get first non-empty value (treats '' as empty, unlike ||)
    const firstNonEmpty = (...values) => values.find(v => v !== undefined && v !== null && v !== '') ?? values[values.length - 1];

    const config = {
      isModal: true, // Default to modal mode
      ...apiConfig,
      ...ui,
      clientPublicToken,
      // Apply resolved theme
      theme: resolvedTheme,
      // Apply theme defaults for colors not explicitly set by API or UI
      // Use firstNonEmpty to properly handle empty strings from API
      primaryColor: firstNonEmpty(ui.primaryColor, apiConfig.primaryColor, themeDefaults.primaryColor),
      modalTriggerIconColor: firstNonEmpty(ui.modalTriggerIconColor, apiConfig.modalTriggerIconColor, themeDefaults.modalTriggerIconColor),
      backgroundColor: firstNonEmpty(ui.backgroundColor, apiConfig.backgroundColor, themeDefaults.backgroundColor),
      textColor: firstNonEmpty(ui.textColor, apiConfig.textColor, themeDefaults.textColor),
      inputBackgroundColor: firstNonEmpty(ui.inputBackgroundColor, apiConfig.inputBackgroundColor, themeDefaults.inputBackgroundColor),
      inputTextColor: firstNonEmpty(ui.inputTextColor, apiConfig.inputTextColor, themeDefaults.inputTextColor),
      inputIconColor: firstNonEmpty(ui.inputIconColor, apiConfig.inputIconColor, themeDefaults.inputIconColor),
      // Item #8794. Every theme defines this and nothing took it, so the
      // placeholder rendered the UA grey on every surface, light DOM included.
      // The apiConfig read is here for shape only: IndexConfigResponse carries no
      // placeholder field today (ingest index.go states the other three), so the
      // theme table is the effective source.
      inputPlaceholderColor: firstNonEmpty(ui.inputPlaceholderColor, apiConfig.inputPlaceholderColor, themeDefaults.inputPlaceholderColor),
      // Item #16445 authored these reads and item #16452 made them reach
      // something: IndexConfigResponse now carries both keys, validated on save
      // as a single CSS length. Resolving to undefined is still the point --
      // themeStyleVars then emits nothing and the CSS fallback decides, so no
      // instance moves until someone sets a value. The dashboard controls that
      // set one are #17168's Appearance sub-group.
      inputFontSize: firstNonEmpty(ui.inputFontSize, apiConfig.inputFontSize, themeDefaults.inputFontSize),
      inputHeight: firstNonEmpty(ui.inputHeight, apiConfig.inputHeight, themeDefaults.inputHeight),
      paginationButtonBgColor: firstNonEmpty(ui.paginationButtonBgColor, apiConfig.paginationButtonBgColor, apiConfig.primaryColor, themeDefaults.primaryColor),
      paginationIconColor: firstNonEmpty(ui.paginationIconColor, apiConfig.paginationIconColor, themeDefaults.paginationIconColor),
      borderColor: firstNonEmpty(ui.borderColor, apiConfig.borderColor, themeDefaults.borderColor),
      // Search placeholder
      searchPlaceholder: firstNonEmpty(ui.searchPlaceholder, apiConfig.searchPlaceholder, 'Search...'),
      // Result row chrome (item #17163). One of each kind, so each takes the
      // resolver its type needs: a length through firstNonEmpty, a boolean
      // through resolveToggle.
      resultRowPadding: firstNonEmpty(ui.resultRowPadding, apiConfig.resultRowPadding, ''),
      resultDividers: resolveToggle(ui.resultDividers, apiConfig.resultDividers, true),
      // Dropdown height cap (item #17162). A length, not a boolean, so it takes
      // firstNonEmpty like the input scale rather than resolveToggle -- and
      // resolving to undefined is the point, so themeStyleVars emits nothing
      // and max-height's own `none` fallback decides.
      dropdownMaxHeight: firstNonEmpty(ui.dropdownMaxHeight, apiConfig.dropdownMaxHeight, ''),
      // The instance's own stylesheet (item #17981). A document, not a token:
      // it is rendered as the last style element in every root rather than
      // emitted as a custom property. Empty renders nothing.
      customCss: firstNonEmpty(ui.customCss, apiConfig.customCss, ''),
      // Which box the suggestion panel is positioned against (item #17166): the
      // bar, or the input's field box, which stops the panel at the submit
      // button's leading edge. An enum over item #17191's contract, so it takes
      // firstNonEmpty like submitButtonIcon rather than resolveToggle.
      //
      // The `...apiConfig` spread above already carries this key, so the line
      // exists for the one case the spread gets wrong: `...ui` over it treats
      // an embed's EMPTY string as an answer, which is what an embed generator
      // emits for a field its author left blank. That would beat the instance's
      // stored anchor and make the dashboard setting unreachable. firstNonEmpty
      // is what reads empty as "the operator did not say".
      dropdownAnchor: firstNonEmpty(ui.dropdownAnchor, apiConfig.dropdownAnchor, ''),
      // Result access glyphs (item #17161). Same shape and same default as the
      // clear control below, and it inherits resolveToggle rather than
      // re-deriving the boolean rule.
      showAccessIcons: resolveToggle(ui.showAccessIcons, apiConfig.showAccessIcons, true),
      // Excerpt line count (i22827): embed override, then the instance's
      // saved value, clamped 1-4 with 2 for unset.
      excerptLines: clampExcerptLines(ui.excerptLines ?? apiConfig.excerptLines),
      // Leading magnifier glyph (item #16449). Default true, same shape and
      // same argument as the clear control below.
      showInputIcon: resolveToggle(ui.showInputIcon, apiConfig.showInputIcon, true),
      // Trailing clear control (item #17160). Default true -- see resolveToggle
      // for why a boolean needs its own resolver rather than firstNonEmpty.
      showClearButton: resolveToggle(ui.showClearButton, apiConfig.showClearButton, true),
      // Optional in-input submit button (item #16450). The default is off, and
      // the other three are only read when it is on, so an instance that
      // configures nothing renders exactly what it rendered before.
      //
      submitButtonEnabled: resolveToggle(ui.submitButtonEnabled, apiConfig.submitButtonEnabled, false),
      submitButtonLabel: firstNonEmpty(ui.submitButtonLabel, apiConfig.submitButtonLabel, ''),
      submitButtonIcon: firstNonEmpty(ui.submitButtonIcon, apiConfig.submitButtonIcon, ''),
      // A CSS property value, not a colour token (decision #5088), so it is
      // emitted as its own custom property in `background` position rather than
      // folded into the theme's colour vars.
      submitButtonFill: firstNonEmpty(ui.submitButtonFill, apiConfig.submitButtonFill, ''),
      // What submitting does, and where (item #17172). These two read the
      // SERVER config only -- no `ui.` term, unlike every other key above.
      //
      // That asymmetry is the point. `...ui` spreads over `...apiConfig`, so an
      // embed snippet supplying ui.searchResultsUrl would outrank the served
      // value and send visitors to an origin item #17171's save-time check
      // never saw. The destination is executed in a visitor's browser on a
      // customer's page, and the page is the last party that should get to
      // name it. Decision #5057: the server owns the policy, the browser only
      // performs the navigation.
      //
      // Defaulted here as well as served through EffectiveSubmitAction, so a
      // bundle newer than its instance reads "none" rather than an empty
      // string the widget has no branch for.
      submitAction: apiConfig.submitAction || 'none',
      searchResultsUrl: apiConfig.searchResultsUrl || '',
      // Extended theme properties
      featuredIconColor: themeDefaults.featuredIconColor,
      lockedIconColor: themeDefaults.lockedIconColor,
      membersIconColor: themeDefaults.membersIconColor,
      errorTextColor: themeDefaults.errorTextColor,
      errorBgColor: themeDefaults.errorBgColor,
      noResultsColor: themeDefaults.noResultsColor,
      backdropColor: themeDefaults.backdropColor,
      // New theme properties
      backdropBlur: themeDefaults.backdropBlur,
      shadowSm: themeDefaults.shadowSm,
      shadowMd: themeDefaults.shadowMd,
      shadowLg: themeDefaults.shadowLg,
      surfaceColor: themeDefaults.surfaceColor,
      surfaceBorderColor: themeDefaults.surfaceBorderColor,
      resultHoverBg: themeDefaults.resultHoverBg,
      resultActiveBg: themeDefaults.resultActiveBg,
      // Item #16451. The theme default is the FALLBACK, not the source: two of
      // the shipped themes set a highlightColor, so resolving theme-first would
      // make a themed instance's colour unsettable. firstNonEmpty reads the
      // theme's null the same way it reads an unset field, and the emission
      // below still falls an unresolved value back to the primary colour, which
      // is the compatibility floor.
      highlightColor: firstNonEmpty(
        ui.highlightColor,
        apiConfig.highlightColor,
        themeDefaults.highlightColor
      ),
      // Item #17165. Empty is the operating value: the CSS chain falls back to
      // --highlight-color, so an instance that sets no underline colour keeps
      // one colour for both rather than us copying the text colour into a
      // second property that could then drift from it.
      highlightUnderlineColor: firstNonEmpty(
        ui.highlightUnderlineColor,
        apiConfig.highlightUnderlineColor,
        ''
      ),
    };

    // The render shape is the authority; the renderer is selected from it. The
    // legacy isModal stays in sync so the existing modal code paths continue to
    // work unchanged.
    //
    // shapeOverride is the ONLY input that outranks server config, and it is a
    // prop -- there is no route to it from a config payload, so a customer
    // embed still cannot disagree with the dashboard (decision #4963 holds).
    // VO's own chrome passes it where the surface, not the instance, owns the
    // shape: the dashboard's header actions slot fits a compact control and
    // nothing else (decision #5050). It is normalized through the same
    // resolver, so a bad value is repaired rather than blanking the widget.
    //
    // Below the instance's breakpoint the narrow shape supplies the shape
    // instead (decision #5057). Both shapes are server config; the viewport
    // only selects between them, which is why this is not a client override.
    // The narrow shape is checked FIRST, because a placement that brings its own
    // collapse means its base shape is the wide-screen half of a pair, not a
    // shape that wins at every width. A pinned first-party slot brings no
    // narrowOverride, so it still wins everywhere exactly as before (#5050).
    if (isNarrowViewport && narrowShape) {
      config.shape = narrowShape;
    } else if (shapeOverride) {
      config.shape = resolveRenderShape(shapeOverride);
    } else {
      config.shape = resolveRenderShape(config);
    }
    config.mode = rendererForShape(config.shape);
    config.isModal = config.mode === 'modal';

    // Must follow the mode resolution above: the page size is surface-dependent
    // and the shape is what decides the surface.
    config.resultsPerPage = resolvePageSize(config);

    // The client token is never logged, even in debug mode.
    debugLog('[vo-search-component] Merged config:', {
      showDefaultResults: config.showDefaultResults,
      resultsPerPage: config.resultsPerPage,
      shape: config.shape,
      mode: config.mode,
      isModal: config.isModal,
      rawTheme: rawTheme,
      resolvedTheme: resolvedTheme,
      theme: config.theme,
      backgroundColor: config.backgroundColor,
      primaryColor: config.primaryColor,
      borderColor: config.borderColor,
      surfaceColor: config.surfaceColor,
    });

    return config;
  }, [apiStable.current, ui, clientPublicToken, systemTheme, shapeOverride, narrowOverride, narrowShape, isNarrowViewport]);

  // Track if modal has been opened at least once
  const [modalHasOpened, setModalHasOpened] = useState(false);

  // Only create the search hook if not modal, or if modal and has been opened, AND API config is loaded
  //
  // ...and once enabled, it stays enabled. An always-open inline instance is
  // enabled from the start, so a visitor can already be searching when the
  // viewport narrows it into an overlay (decision #5057). Dropping back to
  // disabled there threw that search away: opening the overlay had to re-run the
  // query the visitor had already run, flashing empty results in between. The
  // latch never resets, which is correct -- results the visitor has seen do not
  // become unfetched.
  const searchWasEnabled = useRef(false);
  const enabledNow = (!merged.isModal || modalHasOpened) && !!apiStable.current;
  if (enabledNow) searchWasEnabled.current = true;
  const enabled = enabledNow || (searchWasEnabled.current && !!apiStable.current);
  const search = use_search(merged, initialState.query, initialState.page, enabled);

  // Overlay visibility. An overlay always starts closed: the visitor opens it
  // from the trigger. Search that is open from the start is the inpage surface.
  const [modalVisible, setModalVisible] = useState(false);

  // The overlay's current layout (modal|tile), driven by the open event's mode
  // so a single shared instance can render either. Multi-mode widget (#5414).
  const [overlayMode, setOverlayMode] = useState(merged.mode === 'tile' ? 'tile' : 'modal');

  // ...and re-derived whenever the shape itself changes, which is what crossing
  // the breakpoint does (decision #5057). Initialised once, this state outlived
  // its shape: an overlay open on a desktop kept rendering the list after the
  // viewport narrowed into a grid layout. An open event still wins afterwards,
  // because it names the layout the trigger asked for.
  useEffect(() => {
    setOverlayMode(merged.mode === 'tile' ? 'tile' : 'modal');
  }, [merged.mode]);

  // This instance acts as the shared overlay host when it's a modal instance or
  // when its trigger is placed externally (hideTrigger) -- i.e. the single
  // instance the drop-in triggers dispatch open events to.
  const isOverlayHost = merged.isModal || hideTrigger;

  // Listen for open events (drop-in triggers). vo-search-open carries a {mode}
  // payload so the shared overlay renders modal or tile; vo-search-open-modal is
  // the legacy modal-only event kept for cached snippets.
  //
  // A SCOPED overlay does not listen (decision #5074). The event carries no
  // placement identity -- it was designed when a page had one overlay to open --
  // so a scoped panel answering it means one page-scoped trigger anywhere opens
  // every scoped panel on the page. Its own trigger opens it instead, which is
  // the only sender with a defensible claim on it. Page-scoped overlays, which
  // is every embed in the wild, are untouched.
  useEffect(() => {
    if (!isOverlayHost || isScopedOverlay(portalTarget) || typeof window === 'undefined') return;
    const openWith = (mode) => {
      setOverlayMode(mode === 'tile' ? 'tile' : 'modal');
      setModalVisible(true);
      setModalHasOpened(true);
    };
    // The shape is authoritative when the event carries one. A payload with only
    // the legacy token comes from a snippet cached before the shape existed, and
    // that token still selects the layout it always did.
    const onOpen = (e) => {
      const shape = e?.detail?.shape;
      if (shape) {
        openWith(rendererForShape(resolveRenderShape(shape)) === 'tile' ? 'tile' : 'modal');
        return;
      }
      openWith(e?.detail?.mode);
    };
    const onLegacyOpen = () => openWith('modal');
    window.addEventListener('vo-search-open', onOpen);
    window.addEventListener('vo-search-open-modal', onLegacyOpen);
    return () => {
      window.removeEventListener('vo-search-open', onOpen);
      window.removeEventListener('vo-search-open-modal', onLegacyOpen);
    };
  }, [isOverlayHost, portalTarget]);

  // Handle demo user interactions
  useEffect(() => {
    if (!demoMode || typeof window === 'undefined') return;

    const handleUserFocus = () => {
      debugLog('[vo-search] User focus detected - stop demo and clear');
      window.dispatchEvent(new CustomEvent('vo-search-stop-demo-and-clear'));
    };

    const handleUserTyping = () => {
      debugLog('[vo-search] User typing detected - stop demo only');
      window.dispatchEvent(new CustomEvent('vo-search-stop-demo-only'));
    };

    window.addEventListener('vo-search-user-focus', handleUserFocus);
    window.addEventListener('vo-search-user-typing', handleUserTyping);
    
    return () => {
      window.removeEventListener('vo-search-user-focus', handleUserFocus);
      window.removeEventListener('vo-search-user-typing', handleUserTyping);
    };
  }, [demoMode]);

  // Handle escape key for both modal and inline modes
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        // If in demo mode, stop demo and clear search for fresh start
        if (demoMode) {
          window.dispatchEvent(new CustomEvent('vo-search-user-focus'));
        }
        
        if (search.query) {
          search.clear_query();
        } else if (merged.isModal) {
          setModalVisible(false);
          merged.onClose?.();
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [search, merged.isModal, merged.onClose, demoMode]);

  // Add ref for search results container to implement scroll to top
  const searchResultsRef = useRef(null);

  // Function to scroll results to top
  const scrollResultsToTop = useCallback(() => {
    if (typeof document === 'undefined' || !searchResultsRef.current) return;
    
    // Find the closest scrollable container
    let scrollContainer = searchResultsRef.current;

    // Go up the DOM tree looking for scrollable parents
    while (scrollContainer &&
      scrollContainer !== document.body &&
      (scrollContainer.scrollHeight <= scrollContainer.clientHeight ||
        getComputedStyle(scrollContainer).overflowY !== 'auto')) {
      scrollContainer = scrollContainer.parentElement;
    }

    // If we found a scrollable container, scroll it to top
    if (scrollContainer && scrollContainer !== document.body) {
      scrollContainer.scrollTop = 0;
    } else {
      // Fallback to scrolling the search results container itself
      searchResultsRef.current.scrollTop = 0;
    }
  }, []);

  // Effect to scroll to top when search results change
  useEffect(() => {
    if (search.results?.hits?.length > 0) {
      scrollResultsToTop();
    }
  }, [search.results, scrollResultsToTop]);

  // Add scroll event listener for demo mode
  useEffect(() => {
    if (!demoMode || typeof document === 'undefined') return;

    const handleScroll = () => {
      debugLog('[vo-search] User scrolling detected - stop demo');
      window.dispatchEvent(new CustomEvent('vo-search-user-scroll'));
    };

    const searchResultsElement = searchResultsRef.current;
    if (searchResultsElement) {
      searchResultsElement.addEventListener('scroll', handleScroll);
      return () => {
        searchResultsElement.removeEventListener('scroll', handleScroll);
      };
    }
  }, [demoMode, search.results]);

  // Wrap the original pagination functions to add scroll to top
  const handlePrevPage = useCallback(() => {
    search.set_page(search.page - 1);
  }, [search]);

  const handleNextPage = useCallback(() => {
    search.set_page(search.page + 1);
  }, [search]);

  // Don't render until API config is loaded. If the config fetch failed after
  // its retry, render a friendly error state instead of vanishing silently.
  if (!apiStable.current) {
    if (configError) {
      debugLog('[vo-search-component] Config fetch failed; rendering error state');
      return <SearchConfigError />;
    }
    debugLog('[vo-search-component] API config not loaded yet, returning null');
    return null;
  }

  // Compute RGB for color values for use in rgba()
  // Handles both hex (#fff, #ffffff) and rgba(r, g, b, a) formats
  function colorToRgb(color) {
    if (!color) return '34,34,34';

    // Handle rgba() or rgb() format
    const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (rgbaMatch) {
      return `${rgbaMatch[1]},${rgbaMatch[2]},${rgbaMatch[3]}`;
    }

    // Handle hex format
    let c = color.replace('#', '');
    if (c.length === 3) {
      c = c.split('').map(x => x + x).join('');
    }
    const num = parseInt(c, 16);
    return [(num >> 16) & 255, (num >> 8) & 255, num & 255].join(',');
  }
  const textColorRgb = merged.textColor ? colorToRgb(merged.textColor) : '34,34,34';
  const inputTextColorRgb = merged.inputTextColor ? colorToRgb(merged.inputTextColor) : '34,34,34';
  const paginationIconColorRgb = merged.paginationIconColor ? colorToRgb(merged.paginationIconColor) : '255,255,255';
  const inputIconColorRgb = merged.inputIconColor ? colorToRgb(merged.inputIconColor) : '136,136,136';

  const handle_result_click = async (result, position = 0) => {
    // If in demo mode, stop demo when user clicks on a result
    if (demoMode) {
      debugLog('[vo-search] User clicked result - stop demo');
      window.dispatchEvent(new CustomEvent('vo-search-user-click'));
    }

    // openInNewTab (#16278): the tab is opened BEFORE the click is tracked, and
    // the ordering is the behaviour rather than an accident of how this reads.
    // A popup blocker only honours window.open inside the synchronous part of a
    // user-gesture handler, and `await track_click` is past that boundary.
    //
    // The anchor's own target=_blank cannot do this job: handleClick calls
    // preventDefault(), so a left click never reaches the anchor. Removing that
    // preventDefault instead would break clicks that land on the card's padding
    // rather than on the link.
    const wantsNewTab = merged.openInNewTab && typeof window !== 'undefined';
    const newTab = wantsNewTab ? window.open(result.url, '_blank', 'noopener,noreferrer') : null;

    await search.track_click(result, position);

    if (typeof window !== 'undefined' && !newTab) {
      // Same tab, either because the instance did not ask for a new one or
      // because a popup blocker refused. Leaving a refused popup unhandled
      // would make the click do nothing at all, which is worse than the
      // behaviour being replaced.
      window.location.href = result.url;
    }
  };

  // Theme CSS custom properties shared by the modal/inline containers so both
  // render with the same colors, shadows and surfaces. Multi-mode widget (#5414).
  const primaryColorRgb = hexToRgbTriple(merged.primaryColor);
  const themeStyleVars = {
        // Primary/accent colors
        '--primary-color': merged.primaryColor,
        // The triple beside the colour, for the one declaration that needs an
        // alpha over the accent (the attached bar's focus ring). Read since
        // #16450 and emitted by nothing until item #17764, so every instance
        // wore the fallback -- VO purple -- on a customer's page.
        ...(primaryColorRgb ? { '--primary-color-rgb': primaryColorRgb } : {}),
        ...(merged.modalTriggerIconColor ? { '--modal-trigger-icon-color': merged.modalTriggerIconColor } : {}),

        // Lengths, emitted only when configured, so the CSS fallback stays the
        // single default (items #16445 and #17162).
        //
        // The reason given here since #16445 was that an empty custom property
        // is a SET property resolving to nothing rather than to the
        // declaration's fallback. That is true of raw CSS -- `--x: ;` -- and
        // NOT of this path, measured while writing #17162: React puts custom
        // properties through CSSOM setProperty, and setProperty(name, '')
        // removes the property. Rendering `{'--x': '', '--y': '5px'}` leaves
        // cssText as `--y: 5px;` with style.length 1.
        //
        // So the conditional is intent rather than protection: it says these
        // have exactly one default and it lives in the CSS. Worth keeping for
        // that, and worth not claiming a mechanism that is not operating.
        ...(merged.inputFontSize ? { '--vo-search-input-font-size': merged.inputFontSize } : {}),
        ...(merged.inputHeight ? { '--vo-search-input-height': merged.inputHeight } : {}),
        ...(merged.dropdownMaxHeight
          ? { '--vo-search-dropdown-max-height': merged.dropdownMaxHeight }
          : {}),
        ...(merged.resultRowPadding
          ? { '--vo-search-result-padding': merged.resultRowPadding }
          : {}),

        // Result dividers (item #17163), and the one property here emitted for
        // a FALSE rather than for a value. Dividers on is the default and it
        // already lives in the rule's own 1px fallback, so expressing it here
        // as well would be a second copy of the default, free to drift from the
        // first. Off is the only state that needs saying, and it says it as a
        // zero width.
        ...(merged.resultDividers ? {} : { '--vo-search-result-divider-width': '0' }),

        // The leading padding (item #16449), emitted for a FALSE for the same
        // reason as the divider width above: the glyph is on by default, that
        // default already lives in the rule's 40px fallback, and off is the
        // only state that needs saying. 12px is the inset the glyph itself sat
        // at, so the text starts where the glyph started rather than flush
        // against the border.
        ...(merged.showInputIcon ? {} : { '--vo-search-input-leading-padding': '12px' }),

        // Submit button fill (item #16450), emitted on the same terms and for
        // the same reason: an empty custom property is a SET property that
        // resolves to nothing rather than to the declaration's fallback, so
        // writing '' here would blank the button's background on every instance
        // that enables one without choosing a fill.
        ...(merged.submitButtonFill ? { '--vo-search-submit-fill': merged.submitButtonFill } : {}),

        // Core UI colors
        '--background-color': merged.backgroundColor,
        '--text-color': merged.textColor,
        '--text-color-rgb': textColorRgb,
        '--input-background-color': merged.inputBackgroundColor || '#fff',
        '--input-text-color': merged.inputTextColor || '#222',
        '--input-text-color-rgb': inputTextColorRgb,
        '--input-placeholder-color': merged.inputPlaceholderColor || '#9ca3af',
        '--input-icon-color': merged.inputIconColor || '#888',
        '--input-icon-color-rgb': inputIconColorRgb,
        '--pagination-button-bg-color': merged.paginationButtonBgColor || '#712cf9',
        '--pagination-icon-color': merged.paginationIconColor || '#fff',
        '--pagination-icon-color-rgb': paginationIconColorRgb,
        '--border-color': merged.borderColor || '#e0e0e0',

        // Feature icon colors (theme-aware)
        '--featured-icon-color': merged.featuredIconColor || '#f6ad55',
        '--locked-icon-color': merged.lockedIconColor || '#3182ce',
        '--members-icon-color': merged.membersIconColor || '#718096',

        // Feedback colors (theme-aware)
        '--error-text-color': merged.errorTextColor || '#e53e3e',
        '--error-bg-color': merged.errorBgColor || '#fff5f5',
        '--no-results-color': merged.noResultsColor || '#888',

        // Modal backdrop (theme-aware)
        '--backdrop-color': merged.backdropColor || 'rgba(0, 0, 0, 0.7)',

        // Result styling
        '--result-hover-bg': merged.resultHoverBg || '#f9fafb',
        '--result-active-bg': merged.resultActiveBg || '#f3f4f6',
        '--highlight-color': merged.highlightColor || merged.primaryColor,
        ...(merged.highlightUnderlineColor
          ? { '--highlight-underline-color': merged.highlightUnderlineColor }
          : {}),

        // Shadows
        '--shadow-sm': merged.shadowSm || '0 1px 2px rgba(0, 0, 0, 0.04)',
        '--shadow-md': merged.shadowMd || '0 4px 6px -1px rgba(0, 0, 0, 0.08)',
        '--shadow-lg': merged.shadowLg || '0 10px 15px -3px rgba(0, 0, 0, 0.08)',

        // Glass morphism (for vo-dark theme)
        '--backdrop-blur': merged.backdropBlur || '4px',
        '--surface-color': merged.surfaceColor || '#ffffff',
        '--surface-border-color': merged.surfaceBorderColor || '#e5e7eb'
  };

  // The inpage surface is this same search UI rendered in the document flow:
  // no titlebar, no trigger, always open, nothing overlaying the page. Its
  // layout chooses the results presentation, so an inpage grid is the tile
  // cards in flow rather than in a fullscreen overlay. The classes are what a
  // host page targets to size the surface (item #8353).
  const isInPage = merged.mode === 'inpage';
  const inPageLayout = merged.shape?.layout === 'grid' ? 'grid' : 'list';
  const inPageClasses = isInPage ? ` vo-search-inpage vo-search-inpage-${inPageLayout}` : '';

  const search_component = (
    <div
      className={`vo-search-search-modal${merged.isModal ? ' vo-search-modal-enabled' : ''}${modalVisible ? ' vo-search-active' : ''}${inPageClasses}`}
      data-theme={merged.theme || 'light'}
      style={themeStyleVars}
    >
      {merged.isModal && (
        <div className="vo-search-search-titlebar">
          <button
            className="vo-search-search-close-button"
            onClick={() => {
              setModalVisible(false);
              merged.onClose?.();
            }}
            aria-label="Close search"
          >
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      )}
      {enabled && search && (
        <>
          <SearchInput
            query={search.query}
            onQueryChange={search.set_query}
            onClear={search.clear_search}
            autoFocus={merged.autoFocus}
            primaryColor={merged.primaryColor}
            inputIconColor={merged.inputIconColor}
            placeholder={merged.searchPlaceholder}
            demoMode={demoMode}
            demoTransport={demoTransport}
            showInputIcon={merged.showInputIcon}
            showClearButton={merged.showClearButton}
            submitButtonEnabled={merged.submitButtonEnabled}
            submitButtonLabel={merged.submitButtonLabel}
            submitButtonIcon={merged.submitButtonIcon}
          />
          <div className="vo-search-search-body" ref={searchResultsRef}>
            <SearchResults
              results={search.results}
              isLoading={search.is_loading}
              error={search.error}
              onResultClick={handle_result_click}
              primaryColor={merged.primaryColor}
              displayTags={merged.displayTags}
              variant={isInPage && inPageLayout === 'grid' ? 'tile' : 'list'}
              tileColumns={merged.tileColumns}
              showHeroImage={merged.showHeroImage}
              showExcerpts={merged.showExcerpts}
              excerptLines={merged.excerptLines}
              showThumbnails={merged.showThumbnails}
              showAccessIcons={merged.showAccessIcons}
              openInNewTab={merged.openInNewTab}
            />
            {merged.enablePagination && (
              <SearchPagination
                page={search.page}
                hasPreviousPage={search.has_previous_page()}
                hasNextPage={search.has_next_page()}
                onPreviousPage={handlePrevPage}
                onNextPage={handleNextPage}
                paginationButtonBgColor={merged.paginationButtonBgColor}
              />
            )}
          </div>
        </>
      )}
      {!merged.hideAttribution && (
        <div className="vo-search-search-footer">
          <div className="vo-search-attribution">
            <span className="vo-search-attribution-label">Search by{' '}</span>
            <a
              href="https://visitingobjects.com"
              className="vo-search-attribution-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              Visiting Objects
            </a>
          </div>
        </div>
      )}
    </div>
  );

  // Tile layout rendered inside the shared overlay when opened in tile mode.
  const tile_overlay_content = (
    <div className="vo-search-tile-overlay-inner" style={{ display: 'flex', flexDirection: 'column', maxHeight: '100%', overflow: 'hidden' }}>
      {enabled && search && (
        <>
          <SearchInput
            query={search.query}
            onQueryChange={search.set_query}
            onClear={search.clear_search}
            autoFocus={merged.autoFocus}
            primaryColor={merged.primaryColor}
            inputIconColor={merged.inputIconColor}
            placeholder={merged.searchPlaceholder}
            demoMode={demoMode}
            demoTransport={demoTransport}
            showInputIcon={merged.showInputIcon}
            showClearButton={merged.showClearButton}
            submitButtonEnabled={merged.submitButtonEnabled}
            submitButtonLabel={merged.submitButtonLabel}
            submitButtonIcon={merged.submitButtonIcon}
          />
          <div className="vo-search-tile-overlay-body" style={{ overflowY: 'auto', padding: '16px' }}>
            <SearchResults
              results={search.results}
              isLoading={search.is_loading}
              error={search.error}
              onResultClick={handle_result_click}
              primaryColor={merged.primaryColor}
              displayTags={merged.displayTags}
              variant="tile"
              tileColumns={merged.tileColumns}
              showHeroImage={merged.showHeroImage}
              showExcerpts={merged.showExcerpts}
              excerptLines={merged.excerptLines}
              showThumbnails={merged.showThumbnails}
              showAccessIcons={merged.showAccessIcons}
              openInNewTab={merged.openInNewTab}
            />
          </div>
        </>
      )}
    </div>
  );

  // The shared overlay renders the modal layout or the fullscreen-tile layout
  // depending on the mode carried by the open event. Multi-mode widget (#5414).
  const overlayContent = overlayMode === 'tile' ? tile_overlay_content : search_component;

  // Inpage surface: the search UI sits in the page, so it is returned before
  // the overlay host is considered. There is no trigger to hide and no overlay
  // to open -- an inpage instance is never an overlay host, even when the page
  // also asks for external triggers.
  //
  // It carries its own styles (decision #5050). Sitting in the flow is what
  // this surface IS, so unlike modal and tile it cannot portal to document.body
  // to reach the head-injected vo-search.css -- and inside a shadow root that
  // stylesheet cannot cross the boundary. Without these two blocks the surface
  // renders with no box, no height cap and no internal scroller, and its result
  // rows with no padding, separator or type scale.
  if (isInPage) {
    // ...unless this instance IS the shared overlay host. hideTrigger marks the
    // instance createSearchInstance bootstraps to receive vo-search-open events
    // from drop-in triggers. An inpage instance dispatches none and has no
    // overlay, so the host has nothing to host -- and since VoSearchShadowRoot
    // appends its own container to document.body, rendering the surface here
    // put a second always-open search at the bottom of the page.
    if (hideTrigger) return null;

    // The reset goes FIRST. It sits at specificity 0, exactly like the mirrors
    // it precedes, so source order is the only thing that lets their
    // margin-bottom / margin-left rules survive its `margin: 0`.
    return (
      <>
        <ResponsiveStyles css={SHADOW_SURFACE_RESET_CSS} />
        <ResponsiveStyles css={INPAGE_SURFACE_CSS} />
        <ResponsiveStyles css={SEARCH_INPUT_CSS} />
        <ResponsiveStyles css={INLINE_RESULTS_CSS} />
        <ResponsiveStyles css={THEME_VARIANT_CSS} />
        <InstanceStyles css={merged.customCss} />
        {search_component}
      </>
    );
  }

  // Shared overlay host (modal instance or externally-triggered). Renders the
  // modal or tile layout based on the event-driven overlayMode.
  if (isOverlayHost) {
    // A SCOPED overlay is a sibling of the trigger inside the same mount rather
    // than a layer over the document, and the container's own background is
    // transparent -- so the close control and the open control are drawn on top
    // of one another: measured 30x30 at x=1160 against 20x20 at x=1185, boxes
    // overlapping by 5x20px (item #8771, decision #5081). A page-scoped overlay
    // covers the document behind its own backdrop and is left alone.
    const scopedOverlayOpen = isScopedOverlay(portalTarget) && modalVisible;
    const modalContent = (
      <div
        className={`vo-search-modal-container vo-search-modal-enabled vo-search-active${
          isScopedOverlay(portalTarget) ? ' vo-search-overlay-scoped' : ''
        }`}
        // Containment rides INLINE for the same reason the tile overlay's
        // always has (item #8737). The SCOPED_OVERLAY_CSS mirror carries an
        // absolute box too, but every mirrored selector is :where()-wrapped at
        // specificity 0 by a standing contract -- so inside a shadow root,
        // where the head-injected vo-search.css cannot reach, the mirror is
        // unopposed and wins, while in LIGHT DOM `.vo-search-modal-container {
        // position: fixed }` outranks it and the overlay measures the viewport
        // again. That is the demo bundle's path, which mounts straight into its
        // container. Inline, no stylesheet can outrank it and the mirror's
        // zero-specificity contract does not have to bend.
        //
        // `--backdrop-color` is declared HERE and nowhere else on this path
        // (item #8787). themeStyleVars land on the card, which is a sibling of
        // the backdrop's subtree, and custom properties inherit down rather
        // than sideways -- so every theme's configured backdropColor resolved
        // to the hard-coded fallback in the backdrop's own `var()` and no popup
        // in the wild had ever been tinted by its theme (decision #5088). The
        // container is the nearest common ancestor of the backdrop and the
        // card, so declaring it here reaches both without moving anything.
        // Only this one property: the card stays the theming root for the rest,
        // and hoisting the whole bag would change what inherits where for no
        // reason.
        style={{
          '--backdrop-color': themeStyleVars['--backdrop-color'],
          ...(isScopedOverlay(portalTarget) ? { position: 'absolute' } : {}),
        }}
      >
        {/*
          A page-scoped overlay lands on document.body and is styled by the
          head-injected vo-search.css, exactly as every overlay in the wild is.
          A scoped one stays inside its placement's shadow root, where that
          stylesheet cannot reach -- decision #5064's test -- so it carries the
          mirrors with it (item #8679).
        */}
        {isScopedOverlay(portalTarget) && <ScopedOverlayStyles customCss={merged.customCss} />}
        {/*
          The instance stylesheet is not a mirror, so it travels with the
          overlay either way (item #17981): scoped, it is the last block above;
          page-scoped, it lands in the light DOM after the head-injected
          vo-search.css and wins the ties at equal specificity.
        */}
        {!isScopedOverlay(portalTarget) && <InstanceStyles css={merged.customCss} />}
        <div
          className="vo-search-search-modal-backdrop vo-search-active"
          // The tint is fixed by the stylesheet for the same reason the
          // container is, and needs the same inline containment when scoped
          // (item #8737): otherwise the popup lands correctly inside its host
          // while the dimming covers the whole page around it.
          style={isScopedOverlay(portalTarget) ? { position: 'absolute' } : undefined}
          onClick={() => {
            setModalVisible(false);
            merged.onClose?.();
          }}
        />
        <div
          className="vo-search-search-modal-content vo-search-active"
          onClick={e => e.stopPropagation()}
          style={{
            position: 'relative',
            width: '100%',
            // Scoped, the wrapper has to span the container: the card inside is
            // absolutely positioned and contributes no height, so as a plain
            // flex item the wrapper measures zero -- and the mirror's
            // `max-height: 100%` on the card then resolves against zero and
            // collapses it to its borders (item #8737, measured 2px). A page
            // overlay is left alone; the stylesheet has always sized it.
            ...(isScopedOverlay(portalTarget) ? { height: '100%' } : {}),
            color: merged.modalTextColor,
            backgroundColor: merged.modalBackgroundColor
          }}
        >
          {overlayContent}
        </div>
      </div>
    );

    return (
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        {modalVisible && typeof document !== 'undefined' &&
          ReactDOM.createPortal(modalContent, overlayPortalTarget(portalTarget))}
        {!hideTrigger && merged.shape?.trigger === 'bar' && (
          <BarTrigger
            merged={merged}
            hidden={scopedOverlayOpen}
            onOpen={() => {
              setModalVisible(true);
              setModalHasOpened(true);
            }}
          />
        )}
        {!hideTrigger && merged.shape?.trigger !== 'bar' && (
          <>
            {/*
              The overlay above portals to document.body and picks up the
              head-injected vo-search.css; this button does not move, so inside
              a pinned shape's shadow root nothing styles it (decision #5064).
              The block is rendered here rather than at the top of the branch so
              it exists exactly when the button does.
            */}
            <ResponsiveStyles css={TRIGGER_CSS} />
            <button
              type="button"
              className="vo-search-search-modal-trigger"
              style={{
                '--primary-color': merged.primaryColor,
                ...(merged.modalTriggerIconColor ? { '--modal-trigger-icon-color': merged.modalTriggerIconColor } : {}),
                // Hidden, not unmounted: the trigger holds its place in the
                // host's layout, and `visibility: hidden` takes it out of the
                // tab order too, so it cannot be reached behind the overlay.
                ...(scopedOverlayOpen ? { visibility: 'hidden' } : {})
              }}
              onClick={() => {
                setModalVisible(true);
                setModalHasOpened(true);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke={merged.modalTriggerIconColor || '#ffffff'}
                strokeWidth="2"
                width="20"
                height="20"
                style={{ display: 'block' }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  fill="none"
                />
              </svg>
            </button>
          </>
        )}
      </div>
    );
  }

  // Tile mode: trigger opens a fullscreen overlay rendering the tile grid.
  if (merged.mode === 'tile') {
    return (
      <TileOverlay
        search={search}
        merged={merged}
        enabled={enabled}
        demoMode={demoMode}
        demoTransport={demoTransport}
        onResultClick={handle_result_click}
        styleVars={themeStyleVars}
        hideTrigger={hideTrigger}
        portalTarget={portalTarget}
      />
    );
  }

  // Inline mode: input with an absolutely-positioned instant-results dropdown.
  if (merged.mode === 'inline') {
    return (
      <InlineSearch
        search={search}
        merged={merged}
        enabled={enabled}
        demoMode={demoMode}
        demoTransport={demoTransport}
        onResultClick={handle_result_click}
        styleVars={themeStyleVars}
      />
    );
  }

  // Non-modal, non-inline (e.g. tile before its overlay lands, or a plain
  // embedded search): render the always-visible search component.
  return search_component;
}

export { VisitingObjectsSearch };